import { FormControl, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Slider } from '@/components/ui/slider';
import { derivePainStatus, PAIN_STATUS_LABEL } from '@/lib/clients/roster-fields';

export interface PainLevelNrsSliderFieldProps {
  /** `undefined` = never recorded — deliberately NOT the same claim as
   *  "pain-free" (NRS 0); see normalizePainLevelNrs's doc comment. */
  value?: number;
  onChange: (value: number) => void;
  /** Optional `data-testid` on the Slider's thumb root. Omit for forms that
   *  never had one (e.g. NewClientPage, ClientEditPage). */
  testId?: string;
}

/**
 * Shared 0–10 clinical NRS pain-level slider field (Phase 6.3). Single source
 * for the label (derived pain status in the label text + valueText for a11y),
 * slider, and 0/5/10 anchor row — previously hand-duplicated across
 * ClientFormSheet, NewClientPage, and ClientEditPage.
 */
export function PainLevelNrsSliderField({ value: rawValue, onChange, testId }: PainLevelNrsSliderFieldProps) {
  const recorded = rawValue !== undefined;
  const value = rawValue ?? 0;
  const status = derivePainStatus(value);
  return (
    <FormItem>
      <FormLabel>
        Pain level (NRS):{' '}
        {recorded ? (
          <span className="font-semibold text-primary">
            {value}/10{status ? ` · ${PAIN_STATUS_LABEL[status]}` : ''}
          </span>
        ) : (
          <span className="font-normal text-muted-foreground">
            Not recorded — move the slider to record
          </span>
        )}
      </FormLabel>
      <FormControl>
        <Slider
          min={0}
          max={10}
          step={1}
          value={[value]}
          valueText={
            recorded
              ? `${value}/10${status ? ` · ${PAIN_STATUS_LABEL[status]}` : ''}`
              : 'Not recorded'
          }
          aria-label="Pain level"
          data-testid={testId}
          onValueChange={(v) => onChange(v[0])}
        />
      </FormControl>
      <div className="flex justify-between text-xs text-muted-foreground px-0.5">
        <span>0 · No pain</span>
        <span>5 · Some pain</span>
        <span>10 · Worst</span>
      </div>
      <FormMessage />
    </FormItem>
  );
}
